import React, { useState, useEffect, useCallback } from 'react';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const INTERVIEW_TYPES = ['CODING', 'DSA', 'SYSTEM_DESIGN', 'RESUME', 'BEHAVIORAL', 'HR'];
const DIFFICULTIES = ['easy', 'medium', 'hard'];

const TYPE_COLORS = {
    CODING: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    DSA: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    SYSTEM_DESIGN: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    RESUME: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    BEHAVIORAL: 'bg-green-500/20 text-green-400 border-green-500/30',
    HR: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    MIXED: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const PLAN_STATUS_COLORS = {
    today: 'border-l-primary bg-primary/5',
    upcoming: '',
    past: 'bg-muted/30 opacity-70',
};

const DIFF_COLORS = {
    easy: 'bg-green-500/10 text-green-400',
    medium: 'bg-amber-500/10 text-amber-400',
    hard: 'bg-red-500/10 text-red-400',
};

const InterviewCalendar = () => {
    const navigate = useNavigate();

    // Tab: 'calendar' | 'my-plan'
    const [tab, setTab] = useState('calendar');

    // ── Calendar state ──
    const [schedules, setSchedules] = useState([]);
    const [loadingCal, setLoadingCal] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [form, setForm] = useState({ title: '', interview_type: 'CODING', company: 'General', difficulty: 'medium', duration: 30, scheduled_at: '', notes: '' });

    const [enrollments, setEnrollments] = useState([]);
    const [loadingPlan, setLoadingPlan] = useState(true);
    const [activeEnrollIdx, setActiveEnrollIdx] = useState(0);
    const [planSchedule, setPlanSchedule] = useState(null);
    const [allPlanSchedules, setAllPlanSchedules] = useState([]);
    const [loadingSchedule, setLoadingSchedule] = useState(false);

    // ── Calendar fetch ──
    const fetchSchedules = useCallback(async () => {
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const headers = getAuthHeaders();
            const res = await fetch(`${API_BASE_URL}/api/scheduled-interviews`, { headers, credentials: 'include' });
            if (res.ok) setSchedules(await res.json());
        } catch { /* silent */ }
        finally { setLoadingCal(false); }
    }, []);

    // ── My Plan fetch ──
    const fetchEnrollments = useCallback(async () => {
        setLoadingPlan(true);
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const headers = getAuthHeaders();
            const res = await fetch(`${API_BASE_URL}/api/my-plans`, { headers, credentials: 'include' });
            if (res.ok) { 
                const data = await res.json(); 
                const validEnrollments = data || [];
                setEnrollments(validEnrollments); 
                
                // Fetch schedules for all enrollments to show on calendar
                const schedulePromises = validEnrollments.map(e => 
                    fetch(`${API_BASE_URL}/api/my-plans/${e.id}/schedule`, { headers, credentials: 'include' })
                        .then(async r => {
                            if (r.ok) {
                                const s = await r.json();
                                return { plan_name: e.plan_name, enrollmentId: e.id, ...s };
                            }
                            return null;
                        })
                );
                const allSchedules = await Promise.all(schedulePromises);
                setAllPlanSchedules(allSchedules.filter(s => s !== null));
            }
        } catch (err) { console.error("Plan fetch failed", err); }
        finally { setLoadingPlan(false); }
    }, []);

    const fetchPlanSchedule = useCallback(async (enrollmentId) => {
        if (!enrollmentId) return;
        setLoadingSchedule(true);
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const headers = getAuthHeaders();
            const res = await fetch(`${API_BASE_URL}/api/my-plans/${enrollmentId}/schedule`, { headers, credentials: 'include' });
            if (res.ok) setPlanSchedule(await res.json());
        } catch (err) { console.error("Schedule fetch failed", err); }
        finally { setLoadingSchedule(false); }
    }, []);

    useEffect(() => {
        fetchSchedules();
        /*
        Pilot disabled: My Plan tab/data fetch hidden during restricted pilot.
        fetchEnrollments();
        */
    }, [fetchSchedules]);

    useEffect(() => {
        if (enrollments.length > 0 && enrollments[activeEnrollIdx]) {
            fetchPlanSchedule(enrollments[activeEnrollIdx].id);
        }
    }, [enrollments, activeEnrollIdx, fetchPlanSchedule]);

    // ── Calendar handlers ──
    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const authHeaders = getAuthHeaders();
            const res = await fetch(`${API_BASE_URL}/api/scheduled-interviews`, {
                method: 'POST', credentials: 'include',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (res.ok) {
                setShowForm(false);
                setForm({ title: '', interview_type: 'CODING', company: 'General', difficulty: 'medium', duration: 30, scheduled_at: '', notes: '' });
                fetchSchedules();
            }
        } catch { /* silent */ }
    };

    const handleDelete = async (id) => {
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const headers = getAuthHeaders();
            await fetch(`${API_BASE_URL}/api/scheduled-interviews/${id}`, { method: 'DELETE', headers, credentials: 'include' });
            fetchSchedules();
        } catch { /* silent */ }
    };

    // ── Calendar math ──
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const isToday = (day) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

    // Merge all plan sessions into calendar dates
    const getPlanSessionsForDate = (day) => {
        if (!allPlanSchedules || allPlanSchedules.length === 0) return [];
        return allPlanSchedules.flatMap(ps => {
            const allSessions = [...(ps.today || []), ...(ps.upcoming || []), ...(ps.past || [])];
            return allSessions
                .filter(s => {
                    const dStr = s.scheduled_date || s.scheduled_at?.Time || s.scheduled_at || s.Time;
                    if (!dStr) return false;
                    const d = new Date(dStr);
                    return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
                })
                .map(s => ({ ...s, plan_name: ps.plan_name, enrollmentId: ps.enrollmentId }));
        });
    };

    const getSchedulesForDate = (day) => {
        return schedules.filter(s => {
            const d = new Date(s.scheduled_at);
            return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
        });
    };

    const unifiedUpcoming = useMemo(() => {
        const now = new Date();
        const manual = (schedules || []).map(s => ({
            ...s,
            type: 'manual',
            date: new Date(s.scheduled_at?.Time || s.scheduled_at)
        }));
        const planBased = (allPlanSchedules || []).flatMap(ps => {
            return [
                ...(ps.today || []),
                ...(ps.upcoming || [])
            ].map(s => {
                const dStr = s.scheduled_date || s.scheduled_at?.Time || s.scheduled_at;
                return {
                    id: `plan-${s.id}`,
                    title: s.topic || s.interview_type,
                    interview_type: s.interview_type,
                    company: 'Course Plan',
                    plan_name: ps.plan_name,
                    scheduled_at: dStr,
                    date: new Date(dStr),
                    type: 'plan',
                    difficulty: s.difficulty,
                    duration: s.duration,
                    enrollmentId: ps.enrollmentId
                };
            });
        });

        return [...manual, ...planBased]
            .filter(e => e.date >= now)
            .sort((a, b) => a.date - b.date)
            .slice(0, 5);
    }, [schedules, allPlanSchedules]);

    // My Plan progress
    const totalSessions = planSchedule ? [(planSchedule.today || []), (planSchedule.upcoming || []), (planSchedule.past || [])].reduce((a, b) => a + b.length, 0) : 0;
    const doneSessions = planSchedule?.past?.length || 0;
    const progress = totalSessions > 0 ? Math.round((doneSessions / totalSessions) * 100) : 0;

    return (
        <div className="min-h-screen bg-background">
            <NavigationHeader />
            <main className="pt-16 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto space-y-6">

                    {/* Tabs + Action */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 p-1 bg-muted rounded-xl w-fit">
                            {[
                                { key: 'calendar', label: '📅 My Calendar', icon: 'Calendar' },
                                /*
                                Pilot disabled: My Plan tab hidden during restricted pilot.
                                { key: 'my-plan', label: '📚 My Plan', icon: 'BookOpen' },
                                */
                            ].map(t => (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t.key ? 'bg-card text-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            {tab === 'calendar' && (
                                <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowForm(!showForm)}>
                                    Schedule Interview
                                </Button>
                            )}
                            {/* Pilot disabled: checkout plan browse action hidden.
                            {tab === 'my-plan' && (
                                <Button variant="outline" onClick={() => navigate('/checkout', { state: { planMode: 'structured' } })}>
                                    Browse Plans
                                </Button>
                            )}
                            */}
                        </div>
                    </div>

                    {/* ── CALENDAR TAB ── */}
                    {tab === 'calendar' && (
                        <>
                            {showForm && (
                                <div className="bg-card border border-border rounded-xl p-6 shadow-lg">
                                    <h3 className="text-lg font-semibold text-foreground mb-4">Schedule New Interview</h3>
                                    <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Title *</label>
                                            <input type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Google DSA Practice" className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Date & Time *</label>
                                            <input type="datetime-local" required value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ''} onChange={e => setForm({ ...form, scheduled_at: new Date(e.target.value).toISOString() })} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Interview Type</label>
                                            <select value={form.interview_type} onChange={e => setForm({ ...form, interview_type: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none">
                                                {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Company</label>
                                            <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="e.g. Google" className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none" />
                                        </div>
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Difficulty</label>
                                            <select value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none">
                                                {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm text-muted-foreground mb-1 block">Duration (min)</label>
                                            <input type="number" min={5} max={120} value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) || 30 })} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                                            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm outline-none resize-none" />
                                        </div>
                                        <div className="md:col-span-2 flex items-center gap-3 justify-end">
                                            <Button variant="outline" type="button" onClick={() => setShowForm(false)}>Cancel</Button>
                                            <Button variant="default" type="submit" iconName="Check" iconPosition="left">Create</Button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Calendar Grid */}
                                <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6 shadow-elevation-2">
                                    <div className="flex items-center justify-between mb-6">
                                        <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} className="p-2 rounded-lg hover:bg-muted transition-colors">
                                            <Icon name="ChevronLeft" size={20} className="text-muted-foreground" />
                                        </button>
                                        <h2 className="text-lg font-bold text-foreground">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
                                        <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} className="p-2 rounded-lg hover:bg-muted transition-colors">
                                            <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-7 gap-1 mb-2">
                                        {WEEKDAYS.map(w => <div key={w} className="text-center text-xs font-medium text-muted-foreground py-2">{w}</div>)}
                                    </div>
                                    <div className="grid grid-cols-7 gap-1">
                                        {Array.from({ length: startOffset }).map((_, i) => <div key={`e-${i}`} className="aspect-square" />)}
                                        {Array.from({ length: daysInMonth }).map((_, i) => {
                                            const day = i + 1;
                                            const daySched = getSchedulesForDate(day);
                                            const dayPlan = getPlanSessionsForDate(day);
                                            const total = daySched.length + dayPlan.length;
                                            const isCurrentDay = isToday(day);
                                            return (
                                                <div key={day} className={`aspect-square rounded-lg border p-1 flex flex-col transition-colors ${isCurrentDay ? 'border-primary bg-primary/5' : total > 0 ? 'border-border bg-card hover:border-primary/30' : 'border-transparent hover:bg-muted/50'}`}>
                                                    <span className={`text-xs font-medium ${isCurrentDay ? 'text-primary' : 'text-muted-foreground'}`}>{day}</span>
                                                    <div className="flex-1 flex flex-col gap-0.5 mt-0.5 overflow-hidden">
                                                        {daySched.slice(0, 1).map((s, idx) => (
                                                            <div key={idx} className={`text-[9px] font-medium px-1 py-0.5 rounded truncate border ${TYPE_COLORS[s.interview_type] || 'bg-muted text-muted-foreground'}`}>{s.title || s.interview_type}</div>
                                                        ))}
                                                        {dayPlan.slice(0, 1).map((s, idx) => (
                                                            <div key={`p-${idx}`} className="text-[9px] font-medium px-1 py-0.5 rounded truncate border bg-indigo-500/20 text-indigo-400 border-indigo-500/30">📚 {s.topic || s.interview_type}</div>
                                                        ))}
                                                        {total > 2 && <span className="text-[9px] text-muted-foreground">+{total - 2}</span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Legend */}
                                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500/30 border border-blue-500/50" /> Manual sessions</span>
                                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-indigo-500/30 border border-indigo-500/50" /> Plan sessions</span>
                                    </div>
                                </div>

                                {/* Sidebar */}
                                <div className="space-y-4">
                                    <div className="bg-card border border-border rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Icon name="Clock" size={18} className="text-primary" />
                                            <h3 className="font-semibold text-foreground">Upcoming Sessions</h3>
                                        </div>
                                        {loadingCal ? (
                                            <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}</div>
                                        ) : unifiedUpcoming.length === 0 ? (
                                            <div className="text-center py-6">
                                                <Icon name="CalendarX" size={32} className="text-muted-foreground mx-auto mb-2 opacity-50" />
                                                <p className="text-sm text-muted-foreground">No upcoming interviews</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {unifiedUpcoming.map(s => {
                                                    const dt = s.date;
                                                    return (
                                                        <div key={s.id} className="p-3 rounded-lg border bg-background border-border group transition-all hover:border-primary/30">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-1.5 mb-1">
                                                                        <p className="text-sm font-bold text-foreground truncatetracking-tight">{s.title}</p>
                                                                        {s.type === 'plan' ? (
                                                                            <span className="text-[8px] font-black bg-indigo-500 text-white px-1 py-0.5 rounded-sm uppercase">Plan</span>
                                                                        ) : (
                                                                            <span className="text-[8px] font-black bg-emerald-500 text-white px-1 py-0.5 rounded-sm uppercase">Live</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${TYPE_COLORS[s.interview_type] || 'bg-muted text-muted-foreground'}`}>{s.interview_type?.replace('_', ' ')}</span>
                                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${DIFF_COLORS[s.difficulty]}`}>{s.difficulty}</span>
                                                                    </div>
                                                                    <p className="text-[10px] text-muted-foreground mt-1.5 font-medium">
                                                                        {dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                                    </p>
                                                                </div>
                                                                <div className="flex flex-col gap-1 shrink-0">
                                                                    <button 
                                                                        onClick={() => {
                                                                            if (s.type === 'plan') {
                                                                                navigate('/interview-setup', {
                                                                                    state: {
                                                                                        planSession: {
                                                                                            interview_type: s.interview_type,
                                                                                            difficulty: s.difficulty,
                                                                                            duration: s.duration,
                                                                                            topic: s.topic,
                                                                                            plan_enrollment_id: s.enrollmentId,
                                                                                            plan_day_id: s.id.replace('plan-', ''),
                                                                                        },
                                                                                    },
                                                                                });
                                                                            } else {
                                                                                navigate(`/interview-setup?type=${s.interview_type}&company=${s.company}&difficulty=${s.difficulty}&duration=${s.duration}`);
                                                                            }
                                                                        }} 
                                                                        className="text-[10px] px-2 py-1.5 rounded bg-primary text-white hover:bg-primary/90 font-bold shadow-sm transition-all"
                                                                    >
                                                                        START
                                                                    </button>
                                                                    {s.type === 'manual' && (
                                                                        <button onClick={() => handleDelete(s.id)} className="text-[9px] font-bold text-destructive/60 hover:text-destructive transition-colors text-center mt-1">CANCEL</button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── MY PLAN TAB ── */}
                    {tab === 'my-plan' && (
                        <div className="space-y-6">
                            {loadingPlan ? (
                                <div className="space-y-4">{[1, 2].map(i => <div key={i} className="h-32 bg-muted rounded-2xl animate-pulse" />)}</div>
                            ) : enrollments.length === 0 ? (
                                <div className="text-center py-20 border-2 border-dashed border-border rounded-2xl">
                                    <Icon name="BookOpen" size={48} className="text-muted-foreground mx-auto mb-4 opacity-40" />
                                    <p className="font-normal text-muted-foreground text-lg mb-2">No Active Plans</p>
                                    <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">Purchase a structured preparation plan to get a day-by-day roadmap that auto-syncs to your calendar.</p>
                                    <Button onClick={() => navigate('/checkout', { state: { planMode: 'structured' } })}>Browse Structured Plans</Button>
                                </div>
                            ) : (
                                <>
                                    {/* Enrollment tabs */}
                                    {enrollments.length > 1 && (
                                        <div className="flex gap-2 flex-wrap">
                                            {enrollments.map((e, i) => (
                                                <button key={e.id} onClick={() => setActiveEnrollIdx(i)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${i === activeEnrollIdx ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                                                    {e.plan_name}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Active Plan Card */}
                                    {enrollments[activeEnrollIdx] && (
                                        <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-2xl p-6">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Icon name="BookOpen" size={16} className="text-indigo-400" />
                                                        <span className="text-xs text-indigo-400 font-mediumtracking-wider">Active Plan</span>
                                                    </div>
                                                    <h2 className="text-xl font-bold text-foreground">{enrollments[activeEnrollIdx].plan_name}</h2>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Started {new Date(enrollments[activeEnrollIdx].purchase_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                                    </p>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="text-3xl font-bold text-foreground">{progress}%</p>
                                                    <p className="text-xs text-muted-foreground">Complete</p>
                                                </div>
                                            </div>
                                            <div className="mt-4 h-2 bg-black/20 rounded-full overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
                                            </div>
                                            <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                                                <span>{doneSessions} completed</span>
                                                <span>{totalSessions - doneSessions} remaining</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Plan Schedule */}
                                    {loadingSchedule ? (
                                        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}</div>
                                    ) : planSchedule && (
                                        <div className="space-y-6">
                                            {(planSchedule.today || []).length > 0 && (
                                                <div>
                                                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                                                        <Icon name="Zap" size={16} className="text-indigo-400" /> Today's Sessions
                                                        <span className="text-xs bg-indigo-600/20 text-indigo-400 px-2 py-0.5 rounded-full">{planSchedule.today.length}</span>
                                                    </h3>
                                                    <div className="space-y-3">
                                                        {planSchedule.today.map((s, i) => (
                                                            <PlanSessionRow
                                                                key={i}
                                                                session={s}
                                                                status="today"
                                                                onStart={() =>
                                                                    navigate('/interview-setup', {
                                                                        state: {
                                                                            planSession: {
                                                                                interview_type: s.interview_type,
                                                                                difficulty: s.difficulty,
                                                                                duration: s.duration,
                                                                                topic: s.topic,
                                                                                plan_enrollment_id: enrollments[activeEnrollIdx]?.id,
                                                                                plan_day_id: s.id,
                                                                            },
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {(planSchedule.upcoming || []).length > 0 && (
                                                <div>
                                                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                                                        <Icon name="Calendar" size={16} className="text-blue-400" /> Upcoming Sessions
                                                        <span className="text-xs bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded-full">{planSchedule.upcoming.length}</span>
                                                    </h3>
                                                    <div className="space-y-3">
                                                        {planSchedule.upcoming.map((s, i) => (
                                                            <PlanSessionRow key={i} session={s} status="upcoming" />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {(planSchedule.past || []).length > 0 && (
                                                <div>
                                                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                                                        <Icon name="CheckCircle2" size={16} className="text-green-400" /> Completed
                                                        <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded-full">{planSchedule.past.length}</span>
                                                    </h3>
                                                    <div className="space-y-3">
                                                        {planSchedule.past.map((s, i) => (
                                                            <PlanSessionRow
                                                                key={i}
                                                                session={s}
                                                                status="past"
                                                                onStart={() =>
                                                                    navigate('/interview-setup', {
                                                                        state: {
                                                                            planSession: {
                                                                                interview_type: s.interview_type,
                                                                                difficulty: s.difficulty,
                                                                                duration: s.duration,
                                                                                topic: s.topic,
                                                                                plan_enrollment_id: enrollments[activeEnrollIdx]?.id,
                                                                                plan_day_id: s.id,
                                                                            },
                                                                        },
                                                                    })
                                                                }
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

// ─── Plan Session Row ─────────────────────────────────────────────────

const DIFF_BADGE = { easy: 'bg-green-500/10 text-green-400', medium: 'bg-amber-500/10 text-amber-400', hard: 'bg-red-500/10 text-red-400' };

const PlanSessionRow = ({ session, status, onStart }) => (
    <div className={`rounded-xl border p-4 transition-all ${status === 'today' ? 'border-indigo-400/40 bg-gradient-to-r from-indigo-500/10 to-purple-500/10' : status === 'past' ? 'border-border/50 bg-muted/20' : 'border-border bg-card'}`}>
        <div className="flex items-center gap-4">
            <div className="w-12 text-center shrink-0">
                <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${status === 'today' ? 'bg-indigo-500 animate-pulse' : status === 'past' ? 'bg-green-500' : 'bg-blue-400'}`} />
                <span className="text-[10px] font-bold text-foreground block">Day {session.day_number}</span>
                <span className="text-[10px] text-muted-foreground">{new Date(session.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{session.topic || 'Interview Session'}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[session.interview_type] || 'bg-muted text-muted-foreground'}`}>{session.interview_type?.replace('_', ' ')}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${DIFF_BADGE[session.difficulty]}`}>{session.difficulty}</span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Icon name="Clock" size={9} /> {session.duration}m</span>
                    {session.is_completed && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-green-500/10 text-green-400">
                            Score: {session.completed_score ?? 0}%
                        </span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {status === 'today' ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-indigo-600 text-white font-bold">🔥 TODAY</span>
                ) : status === 'past' ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-green-500/20 text-green-400 font-semibold">✓ Done</span>
                ) : null}
                {onStart && (
                    <button onClick={onStart} className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors">
                        {status === 'past' ? 'Redo' : 'Start'}
                    </button>
                )}
            </div>
        </div>
    </div>
);

export default InterviewCalendar;
